import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';

// Initialize GraphQL client with public access (API Key for unauthenticated)
const publicClient = generateClient({ authMode: 'apiKey' });
const authClient = generateClient();

// Enhanced cache with longer TTL
const newsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const FEATURED_CACHE_KEY = 'featured_news';
const ALL_NEWS_CACHE_KEY = 'all_news';

// Periodic cache cleanup
setInterval(() => {
  const now = Date.now();
  for (const [key, value] of newsCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      newsCache.delete(key);
    }
  }
}, 60 * 1000); // Cleanup every minute

// GraphQL queries
const LIST_NEWS = `
  query ListNews($filter: ModelNewsFilterInput, $limit: Int, $nextToken: String) {
    listNews(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        content
        source
        source_url
        author
        published_at
        featured_image
        tmdb_id
        media_type
        category
        tags
        status
        is_featured
        is_breaking
        view_count
        bookmark_count
        share_count
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const NEWS_BY_CATEGORY = `
  query NewsByCategoryAndPublished_at($category: String!, $published_at: ModelStringKeyConditionInput, $limit: Int, $nextToken: String) {
    newsByCategoryAndPublished_at(category: $category, published_at: $published_at, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        content
        source
        source_url
        author
        published_at
        featured_image
        tmdb_id
        media_type
        category
        tags
        status
        is_featured
        is_breaking
        view_count
        bookmark_count
        share_count
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const GET_FEATURED_NEWS = `
  query ListNews($filter: ModelNewsFilterInput, $limit: Int, $nextToken: String) {
    listNews(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        content
        source
        source_url
        author
        published_at
        featured_image
        tmdb_id
        media_type
        category
        tags
        status
        is_featured
        is_breaking
        view_count
        bookmark_count
        share_count
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const GET_NEWS_BY_MOVIE = `
  query GetNewsByMovie($tmdbId: String!, $limit: Int, $nextToken: String) {
    getNewsByMovie(tmdbId: $tmdbId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        content
        source
        source_url
        author
        published_at
        featured_image
        tmdb_id
        media_type
        category
        tags
        status
        is_featured
        is_breaking
        view_count
        bookmark_count
        share_count
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const GET_NEWS_BY_ID = `
  query GetNews($id: ID!) {
    getNews(id: $id) {
      id
      title
      description
      content
      source
      source_url
      author
      published_at
      featured_image
      tmdb_id
      media_type
      category
      tags
      status
      is_featured
      is_breaking
      view_count
      bookmark_count
      share_count
      createdAt
      updatedAt
    }
  }
`;

const GET_USER_BOOKMARKS = `
  query GetUserNewsBookmarks($limit: Int, $nextToken: String) {
    getUserNewsBookmarks(limit: $limit, nextToken: $nextToken) {
      id
      userID
      newsID
      createdAt
      news {
        id
        title
        description
        featured_image
        published_at
        source
        author
      }
    }
  }
`;

const TOGGLE_NEWS_BOOKMARK = `
  mutation ToggleNewsBookmark($newsID: ID!) {
    toggleNewsBookmark(newsID: $newsID) {
      id
      userID
      newsID
      createdAt
    }
  }
`;

const TRACK_NEWS_VIEW = `
  mutation TrackNewsView($newsID: ID!, $sessionID: String, $ipAddress: String, $userAgent: String) {
    trackNewsView(newsID: $newsID, sessionID: $sessionID, ipAddress: $ipAddress, userAgent: $userAgent) {
      id
      newsID
      userID
      sessionID
      createdAt
    }
  }
`;

// Cache utilities
const getCachedData = (key) => {
  const cached = newsCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  newsCache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Optimized news operations using GraphQL
export const optimizedNewsApi = {
  // Get published news with aggressive caching - OPTIMIZED
  getPublishedNews: async (filters = {}) => {
    const cacheKey = `${ALL_NEWS_CACHE_KEY}_${JSON.stringify(filters)}`;
    
    // Try cache first
    const cached = getCachedData(cacheKey);
    if (cached) {
      return { success: true, items: cached, fromCache: true };
    }

    try {
      const { limit = 50, nextToken = null } = filters;
      
      // Single batch query with server-side filtering
      const result = await publicClient.graphql({
        query: LIST_NEWS,
        variables: {
          filter: {
            status: { eq: 'published' }
          },
          limit,
          nextToken
        }
      });

      let items = result.data?.listNews?.items || [];
      
      // Sort by published_at (latest first)
      items.sort((a, b) => {
        const dateA = new Date(a.published_at || a.createdAt);
        const dateB = new Date(b.published_at || b.createdAt);
        return dateB - dateA;
      });
      
      setCachedData(cacheKey, items);
      return { success: true, items, fromCache: false };
    } catch (error) {
      console.error('Error getting published news:', error);
      return { success: true, items: [], error: error.message };
    }
  },

  // Get featured news with priority caching
  getFeaturedNews: async () => {
    const cached = getCachedData(FEATURED_CACHE_KEY);
    if (cached) {
      return { success: true, items: cached, fromCache: true };
    }

    try {
      const result = await publicClient.graphql({
        query: GET_FEATURED_NEWS,
        variables: {
          filter: {
            status: { eq: 'published' },
            is_featured: { eq: true }
          },
          limit: 20
        }
      });

      let items = result.data?.listNews?.items || [];
      
      // Sort by published_at (latest first)
      items = items.sort((a, b) => {
        const dateA = new Date(a.published_at || a.createdAt);
        const dateB = new Date(b.published_at || b.createdAt);
        return dateB - dateA;
      });
      
      setCachedData(FEATURED_CACHE_KEY, items);
      
      return { success: true, items, fromCache: false };
    } catch (error) {
      console.error('Error getting featured news:', error);
      return { success: true, items: [], error: error.message };
    }
  },

  // Get news by ID with caching
  getNewsById: async (newsId) => {
    const cacheKey = `news_${newsId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      return { success: true, item: cached, fromCache: true };
    }

    try {
      const result = await publicClient.graphql({
        query: GET_NEWS_BY_ID,
        variables: { id: newsId }
      });

      const item = result.data?.getNews;
      if (item) {
        setCachedData(cacheKey, item);
      }

      return { success: true, item, fromCache: false };
    } catch (error) {
      console.error('Error getting news by ID:', error);
      return { success: false, error: error.message };
    }
  },

  // Get movie news - fetches ONLY movie-specific news
  getMovieNews: async (movieId) => {
    if (!movieId) {
      return { success: false, items: [], error: 'movieId required' };
    }

    const cacheKey = `movie_news_${movieId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      return { success: true, items: cached, fromCache: true };
    }

    try {
      const result = await publicClient.graphql({
        query: LIST_NEWS,
        variables: {
          filter: {
            and: [
              { tmdb_id: { eq: movieId.toString() } },
              { status: { eq: 'published' } }
            ]
          },
          limit: 50
        }
      });

      let items = result.data?.listNews?.items || [];
      
      items.sort((a, b) => {
        const dateA = new Date(a.published_at || a.createdAt);
        const dateB = new Date(b.published_at || b.createdAt);
        return dateB - dateA;
      });
      
      setCachedData(cacheKey, items);
      return { success: true, items, fromCache: false };
    } catch (error) {
      console.error('Error getting movie news:', error);
      return { success: false, items: [], error: error.message };
    }
  },

  // Track view (fire and forget for performance)
  trackNewsView: async (newsId, userId = null) => {
    try {
      const sessionID = `session_${Date.now()}`;
      const userAgent = navigator.userAgent || 'Unknown';
      
      // Fire and forget - don't wait for response
      publicClient.graphql({
        query: TRACK_NEWS_VIEW,
        variables: {
          newsID: newsId,
          sessionID,
          userAgent
        }
      }).catch(err => console.error('View tracking failed:', err));

      return { success: true };
    } catch (error) {
      // Silent fail for view tracking
      return { success: true };
    }
  },

  // Preload news data for instant access
  preloadNews: async () => {
    try {
      // Preload featured news
      await optimizedNewsApi.getFeaturedNews();
      
      // Preload recent news
      await optimizedNewsApi.getPublishedNews({ limit: 20 });
      
      console.log('News data preloaded successfully');
    } catch (error) {
      console.error('Error preloading news:', error);
    }
  },

  // Search news with filters
  searchNews: async (searchParams) => {
    try {
      const { query, dateFilter = 'all', category = 'all', limit = 50 } = searchParams;
      
      const LIST_NEWS_QUERY = `
        query ListNews($filter: ModelNewsFilterInput, $limit: Int) {
          listNews(filter: $filter, limit: $limit) {
            items {
              id
              title
              description
              content
              source
              author
              published_at
              featured_image
              tmdb_id
              category
              status
              is_featured
              is_breaking
              view_count
              createdAt
            }
          }
        }
      `;

      const filter = { status: { eq: 'published' } };

      if (category !== 'all') {
        filter.category = { eq: category };
      }
      
      const result = await publicClient.graphql({
        query: LIST_NEWS_QUERY,
        variables: { filter, limit }
      });

      let items = result.data?.listNews?.items || [];
      
      // Client-side search filtering
      if (query) {
        const searchLower = query.toLowerCase();
        items = items.filter(item => {
          // Search in title and description
          const titleMatch = item.title?.toLowerCase().includes(searchLower);
          const descMatch = item.description?.toLowerCase().includes(searchLower);
          
          // Only search in text content, skip base64 images
          let contentMatch = false;
          if (item.content) {
            // Remove base64 images and HTML tags for cleaner search
            const cleanContent = item.content
              .replace(/<img[^>]*src="data:image[^"]*"[^>]*>/gi, '') // Remove base64 images
              .replace(/<[^>]+>/g, ' ') // Remove HTML tags
              .toLowerCase();
            contentMatch = cleanContent.includes(searchLower);
          }
          
          return titleMatch || descMatch || contentMatch;
        });
      }

      // Client-side date filtering
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate;
        
        switch (dateFilter) {
          case 'today':
            startDate = new Date();
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            startDate = new Date();
            startDate.setDate(startDate.getDate() - 7);
            break;
          case 'month':
            startDate = new Date();
            startDate.setMonth(startDate.getMonth() - 1);
            break;
          case 'year':
            startDate = new Date();
            startDate.setFullYear(startDate.getFullYear() - 1);
            break;
          default:
            startDate = null;
        }

        if (startDate) {
          items = items.filter(item => {
            const itemDate = new Date(item.published_at || item.createdAt);
            return itemDate >= startDate;
          });
        }
      }
      
      // Sort by published_at (latest first)
      items.sort((a, b) => {
        const dateA = new Date(a.published_at || a.createdAt);
        const dateB = new Date(b.published_at || b.createdAt);
        return dateB - dateA;
      });

      return { success: true, items };
    } catch (error) {
      console.error('Error searching news:', error);
      return { success: false, items: [], error: error.message };
    }
  },

  // Get news by category
  getNewsByCategory: async (category, filters = {}) => {
    const cacheKey = `category_${category}_${JSON.stringify(filters)}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      return { success: true, items: cached, fromCache: true };
    }

    try {
      const { limit = 20, nextToken = null } = filters;

      const result = await publicClient.graphql({
        query: NEWS_BY_CATEGORY,
        variables: {
          category,
          limit,
          nextToken
        }
      });

      let items = result.data?.newsByCategoryAndPublished_at?.items || [];
      const publishedItems = items.filter(item => item.status === 'published');
      
      // Sort by published_at (latest first)
      publishedItems.sort((a, b) => {
        const dateA = new Date(a.published_at || a.createdAt);
        const dateB = new Date(b.published_at || b.createdAt);
        return dateB - dateA;
      });
      
      setCachedData(cacheKey, publishedItems);
      return { success: true, items: publishedItems, fromCache: false };
    } catch (error) {
      console.error('Error getting news by category:', error);
      return { success: false, items: [], error: error.message };
    }
  },

  // Clear cache (for admin updates)
  clearCache: () => {
    newsCache.clear();
  },

  // Check if user is authenticated
  isAuthenticated: async () => {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }
};

export default optimizedNewsApi;